/* Copyright (c) 2020-present CloudIO Inc */

import { CommonProps } from '../../../../layout/Sidebar/panels/PropertiesView/CommonProperties';
import { Platform } from '../../../../platform/Platform';
import {
  ComponentManagerFn,
  PropertyDefinitions,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { toolTipProps } from '../../../../utils/commonProps';
import { toOptions } from '../../../../utils/ManagerUtils';
import { defaultValue, PivotTableBaseColumn } from './PivotTableStringColumn';

export const PivotColumnDefaultProps: PropertyDefinitions<
  Omit<PivotTableBaseColumn, keyof CommonProps>
> = {
  alignment: {
    type: 'select',
    label: 'Alignment',
    options: toOptions(['Default', 'Start', 'Center', 'End']),
  },
  resizable: {
    label: 'Resizable',
    type: 'boolean',
    helpText:
      'Check this property to allow a user to resize the columns. Columns can be resized by dragging the edge of the column header to the right or left.',
  },
  sortable: {
    label: 'Sortable',
    type: 'boolean',
    helpText:
      'Check this property to enable the sorting of data in ascending or descending order on click of column header',
  },
  title: {
    label: 'Title',
    type: 'string',
  },
  viewAttribute: {
    label: 'View Attribute',
    type: 'ViewAttributeChooser',
    required: true,
    category: 'Data',
    aliasType: 'STORE',
    getAttributeDataTypes: () => ['String', 'Email', 'UserID', 'YN', 'TF'],
    preventMultiEdit: true,
  },
  columnWidth: {
    label: 'Width',
    type: 'number',
    min: 40,
    max: 600,
    step: 10,
    category: 'Layout',
  },
  formatExpression: {
    label: 'Format Expression',
    type: 'expressionOnly',
    category: 'Data',
    returnType: () => 'string',
    helpText:
      'Format the value of the cell using an expression. The raw value can be accessed using the variable $._.value.',
    placeholder: "e.g. format($._.value,'MM/dd/yyyy')",
  },
  ...toolTipProps,
};

const PivotTableStringColumnManager: ComponentManagerFn<
  'PivotTableStringColumn'
> = (platform: Platform) => {
  return {
    ...baseComponentManager<'PivotTableStringColumn'>(
      platform,
      'PivotTableStringColumn',
    ),
    type: 'PivotTableStringColumn',
    name: 'Pivot Table String Column',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...PivotColumnDefaultProps,
      hideRepeatedValues: {
        label: 'Hide Repeated Values',
        type: 'boolean',
        helpText:
          'Enable this property to hide duplicate values from being displayed on the pivot grid.',
      },
    },
    getDisplayText: (item) => item.props.viewAttribute,
    isPropertyVisible: (property) => {
      return property !== 'formatExpression';
    },
    category: 'Pivot Table',
    icon: { iconName: 'io-text-input', iconType: 'cloudio' },
    description: `**Overview**<br/>
The Pivot Table String Column is the primary component for displaying textual data within a Pivot Table. It is used to render categorical information, names, identifiers, or status flags from your data source. Unlike numeric columns, it focuses on the direct presentation of string values and offers specific features for handling data density in grouped views.

**1. Data Connection**<br/>
* **View Attribute:** This required property binds the column to a field in your Data Store. The component supports a variety of text-based and logical data types:
    * **String:** Standard text entries (e.g., "Product Category").
    * **Email:** Specifically formatted email addresses.
    * **UserID:** System-level user identifiers or usernames.
    * **YN / TF:** Boolean-style flags (Yes/No or True/False) rendered as text strings.

**2. Display & Layout**<br/>
You can customize how the text appears and behaves within the grid structure to optimize readability.
* **Dimensions & Alignment:** You can set a fixed **Column Width** (between 40px and 600px) and control text justification using the **Alignment** property (Start, Center, or End).
* **User Controls:**
    * **Resizable:** If enabled, users can manually drag the column headers in the browser to adjust the width.
    * **Sortable:** If enabled, users can click the column header to toggle the rows between ascending and descending alphabetical order.

**3. Grouping & Presentation**<br/>
* **Hide Repeated Values:** This is a essential feature for pivot-style reporting. When enabled, if consecutive rows contain the same value (e.g., "North Region" appearing in 10 consecutive rows), the component will hide the duplicates and show the value only in the first row. This creates a cleaner, more organized visual effect similar to merged cells in a spreadsheet.

**4. Interactivity**<br/>
* **TriggerOnClick:** The component supports the **TriggerOnClick** child element. 
* **Action:** This allows you to transform static text cells into interactive links. For example, clicking a "Customer Name" or "Serial Number" could navigate the user to a detailed dashboard, open a support ticket, or update a global filter variable.

**5. Best Use Cases**<br/>
* **Categorical Grouping:** Organizing reports by Region, Department, or Product Line using the "Hide Repeated Values" feature.
* **Status Tracking:** Displaying text-based status flags like "In Progress," "Completed," or "On Hold."
* **Contact Lists:** Rendering names and email addresses in a searchable, sortable directory format.
* **Metadata Display:** Showing descriptions or comments associated with specific data records.`,
  };
};

export default PivotTableStringColumnManager;
